import { uuid } from './uuid.js';

export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
  get fieldErrors() {
    return this.details?.fields ?? {};
  }
  get isNetwork() {
    return this.status === 0;
  }
}

function readCookie(name) {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

async function ensureCsrf() {
  if (!readCookie('ld_csrf')) await fetch('/api/auth/me', { credentials: 'same-origin' }).catch(() => {});
}

/**
 * Cliente HTTP da API.
 * - Envia o token CSRF (double-submit) em toda requisição que altera dados.
 * - `idempotencyKey`: repetir a mesma ação (duplo clique, retentativa) não duplica registros.
 * - Erros viram ApiError com mensagem em português pronta para exibir.
 */
export async function api(
  path,
  { method = 'GET', body, idempotencyKey, signal, raw = false } = {},
  retried = false,
) {
  const headers = { accept: 'application/json' };
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && !isForm) headers['content-type'] = 'application/json';
  if (method !== 'GET') {
    await ensureCsrf();
    headers['x-csrf-token'] = readCookie('ld_csrf') ?? '';
  }
  if (idempotencyKey) headers['idempotency-key'] = idempotencyKey;

  let res;
  try {
    res = await fetch(path, {
      method,
      headers,
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
      credentials: 'same-origin',
      signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw new ApiError(
      0,
      'network',
      'Sem conexão com o servidor. Nada foi salvo. Verifique a internet e tente de novo.',
    );
  }

  if (raw && res.ok) return res;
  const isJson = (res.headers.get('content-type') ?? '').includes('application/json');
  const data = isJson ? await res.json().catch(() => null) : null;
  if (res.ok) return data;

  const err = data?.error;
  // Cookie CSRF renovado (ex.: aba antiga): tenta uma vez mais com o token novo.
  if (res.status === 403 && err?.code === 'csrf' && !retried) {
    document.cookie = 'ld_csrf=; Max-Age=0; path=/';
    await ensureCsrf();
    return api(path, { method, body, idempotencyKey, signal, raw }, true);
  }
  if (res.status >= 500 && !err) {
    throw new ApiError(
      res.status,
      'server',
      'O servidor está indisponível no momento. Tente novamente em instantes.',
    );
  }
  throw new ApiError(
    res.status,
    err?.code ?? 'error',
    err?.message ?? 'Não foi possível concluir a operação.',
    err?.details,
  );
}

export const newIdempotencyKey = uuid;

/** Caminho de API dentro do lar ativo. */
export function hpath(householdId, path = '') {
  return `/api/households/${householdId}${path}`;
}

export function qs(params) {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v === undefined || v === null || v === '') continue;
    s.set(k, String(v));
  }
  const str = s.toString();
  return str ? `?${str}` : '';
}
